import { Reveal } from "@rekati/ui";
import { MURAL_BAND } from "../../assets/mural";
import { THEME_LINE } from "../../content/event";
import { useI18n } from "../../content/i18n";
import type { EventFacts } from "./queries";
import { TicketStub } from "./TicketStub";

/** The event name over three lines: first word, the middle, and the last word alone. */
function headlineLines(name: string): string[] {
  const words = name.trim().split(/\s+/);
  if (words.length < 3) return [name];
  return [words[0] ?? "", words.slice(1, -1).join(" "), words[words.length - 1] ?? ""];
}

const CLAIM_TONES = ["bg-paint-blue text-white", "bg-paint-pink text-white", "bg-paint-teal text-white"];

/** "Connecting Systems. Securing Data. Enabling Better Healthcare." as three flat pills. */
function ThemeClaims() {
  const { lang } = useI18n();
  const claims = THEME_LINE[lang].split(/(?<=\.)\s+/);
  return (
    <ul role="list" className="flex flex-wrap gap-2">
      {claims.map((c, i) => (
        <li
          key={c}
          className={`rounded-full px-4 py-1.5 text-sm font-semibold sm:text-base ${CLAIM_TONES[i % CLAIM_TONES.length]}`}
        >
          {c.replace(/\.$/, "")}
        </li>
      ))}
    </ul>
  );
}

export function Hero({ facts }: { facts: EventFacts }) {
  const { t } = useI18n();
  // The headline in the visitor's language; the API name stays the event's record elsewhere.
  const name = t("hero.eventName");
  const lines = headlineLines(name);
  return (
    <section aria-labelledby="hero-title" className="relative overflow-x-clip pt-28 sm:pt-32">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 pb-12 sm:px-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start lg:gap-14 lg:pb-16 xl:grid-cols-[minmax(0,1fr)_380px]">
        <Reveal className="grid content-start gap-7 lg:pt-6">
          <h1
            id="hero-title"
            aria-label={name}
            // Beside the ticket the column is narrower than the page, so the size follows the column there.
            className="text-[clamp(2.5rem,0.9rem+6.6vw,7rem)] leading-[0.92] font-extrabold tracking-[-0.04em] uppercase lg:text-[clamp(4rem,-3rem+11.5vw,6.5rem)]"
          >
            {lines.map((line, i) => (
              <span key={i} aria-hidden="true" className="block text-balance">
                {line}
              </span>
            ))}
          </h1>
          <ThemeClaims />
        </Reveal>
        {/* On a tablet the ticket keeps a ticket's width instead of stretching across the page. */}
        <Reveal delay={0.1} className="w-full sm:max-w-md lg:max-w-none">
          <TicketStub facts={facts} />
        </Reveal>
      </div>
      <img
        src={MURAL_BAND}
        alt=""
        aria-hidden="true"
        decoding="async"
        fetchPriority="low"
        draggable={false}
        className="pointer-events-none block h-auto w-full min-w-[720px] -translate-x-[calc((100%-100vw)/2)] select-none"
      />
    </section>
  );
}
